import React from "react";
import { useAuth } from "../contexts/AuthContext";
import Login from "./Login";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import FloatingMenu from "../components/auth/floatingMenu";

const REDIRECT_WHITELIST = ["/invitations", "/notifications", "/chats"];
const REDIRECT_KEY = "postLoginRedirect";

const peekPostLoginRedirect = (): string | null => {
  try {
    const saved = localStorage.getItem(REDIRECT_KEY);
    if (saved && REDIRECT_WHITELIST.some((path) => saved.startsWith(path))) {
      return saved;
    }
  } catch {}
  return null;
};

const clearPostLoginRedirect = () => {
  try {
    localStorage.removeItem(REDIRECT_KEY);
  } catch {}
};

/**
 * /login — sign-in only. Registration lives in the onboarding wizard at
 * /onboarding; "Sign Up" and the legacy `?action=register` both go there.
 */
const Auth: React.FC = () => {
  const { authState, userData, pendingTwoFactor, initializing } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const actionParam = searchParams.get("action");
  const clearedRedirectRef = React.useRef(false);

  React.useEffect(() => {
    if (
      authState.isAuthenticated &&
      userData?.onboardingComplete &&
      !clearedRedirectRef.current
    ) {
      clearedRedirectRef.current = true;
      clearPostLoginRedirect();
    }
  }, [authState.isAuthenticated, userData?.onboardingComplete]);

  const goToRegister = () => navigate("/onboarding");

  if (actionParam === "register" && !pendingTwoFactor) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!pendingTwoFactor && authState.isAuthenticated && userData?.onboardingComplete) {
    const redirectTo = peekPostLoginRedirect();
    return <Navigate to={redirectTo || "/dashboard"} />;
  }
  if (!pendingTwoFactor && authState.isAuthenticated && userData && !userData.onboardingComplete) {
    return <Navigate to="/onboarding" />;
  }
  // Auth user with no profile doc (registration abandoned mid-flow) resumes onboarding.
  if (
    !pendingTwoFactor &&
    authState.isAuthenticated &&
    !userData &&
    authState.error === "User data not found"
  ) {
    return <Navigate to="/onboarding" />;
  }

  const FullScreenLoader: React.FC<{ isLoading: boolean; message?: string }> = ({
    isLoading,
    message = "Redirecting...",
  }) => {
    if (!isLoading) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md bg-base-100/70">
        <div className="flex flex-col items-center gap-4 p-6 pt-0 rounded-xl">
          <h1 className="text-base-content text-[12rem] font-light font-groteskLogo animate-pulse-custom">
            ^
          </h1>
          <p className="text-md -mt-40 font-medium text-base-content font-grotesk text-center">
            {message}
          </p>
        </div>
      </div>
    );
  };

  if (pendingTwoFactor) {
    return <Login onToggleForm={goToRegister} onGoToRegister={goToRegister} />;
  }

  // Only block on the full-screen loader for the initial auth check. Once that
  // resolves, subsequent authState.loading toggles (e.g. a login submission)
  // must NOT unmount Login — that would wipe in-progress form state and any
  // error toast. Login shows its own inline loading indicator instead.
  if (initializing) {
    return <FullScreenLoader isLoading={true} message="Checking authentication..." />;
  }

  return (
    <div className="bg-base-200">
      <FloatingMenu />
      <div className="auth-overlay">
        <div className="py-16 pt-0">
          <Login onToggleForm={goToRegister} onGoToRegister={goToRegister} />
        </div>
      </div>
    </div>
  );
};

export default Auth;
